import React from 'react';
import { Alert, Box, Button, Group, Loader, Stack, Text } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import { IconAlertCircle } from '@tabler/icons-react';
import {
    configuredSignatureFieldForRole,
    EICF_FIELD_ROLES,
} from '../../../global/entities/EICFTemplates/fieldConfig.ts';
import {
    pickStaffSignatureFieldName,
    isPatientSignatureFieldName,
} from '../../../global/helpers/eicfFillablePdf.ts';
import { useFillablePdfSigning } from '../../../global/component/eicf/useFillablePdfSigning.ts';
import { uploadFileToS3 } from '../../../global/helpers/uploadFileToS3.ts';
import { EICFConsentsApi } from '../../../global/apis/EICFApi/EICFConsentsApi.ts';
import SignatureAdoptModal from '../../../global/component/SignatureAdoptModal/SignatureAdoptModal.tsx';
import { useSubmitEICFPhysicianSignature } from '../../../global/entities/EICFConsents/mutations/useSubmitEICFPhysicianSignature.tsx';
import { IEICFConsent } from '../../../global/entities/EICFConsents/EICFConsent.ts';
import { useSessionProvider } from '../../../global/provider/sessionContext.tsx';

interface EICFFillablePdfCounterSignerProps {
    consent: IEICFConsent;
    onSigned: () => void | Promise<void>;
}

const EICFFillablePdfCounterSigner: React.FC<
    EICFFillablePdfCounterSignerProps
> = ({ consent, onSigned }) => {
    const { t } = useTranslation();
    const { currentUser } = useSessionProvider();
    const submitSignature = useSubmitEICFPhysicianSignature();

    const signing = useFillablePdfSigning({
        sourceUrl: consent.signedPdfUrl,
        fieldConfigJson: consent.templateFieldConfigJson,
        editableRole: EICF_FIELD_ROLES.Hcp,
        // Prefer a signature field the admin explicitly assigned to the HCP; fall
        // back to the name heuristic only when none is configured.
        pickSignatureField: (sigNames, config) =>
            configuredSignatureFieldForRole(
                sigNames,
                config,
                EICF_FIELD_ROLES.Hcp,
            ) ?? pickStaffSignatureFieldName(sigNames),
        isOtherPartySignature: isPatientSignatureFieldName,
        // A click-only counter-signature is allowed when the PDF has no HCP field.
        requireSignaturePlacement: false,
        resolveFillOptions: () => ({ flatten: true }),
        submit: async (signedBytes) => {
            const file = new File([signedBytes], 'consent-signed.pdf', {
                type: 'application/pdf',
            });
            const policy = await EICFConsentsApi.getPhysicianSignedPdfUploadUrl(
                consent.id,
                encodeURIComponent(file.name),
            );
            await uploadFileToS3(policy, file);
            await submitSignature.mutateAsync({
                consentID: consent.id,
                answersJson: '',
                signedPdfS3Key: policy.key,
            });
        },
        onSigned,
        watch: [consent.signedPdfUrl, consent.templateFieldConfigJson],
    });

    return (
        <Stack gap="md">
            <Text fw={600}>{t('eicf.hcp.pdfReviewInstructions')}</Text>
            {signing.error && (
                <Alert
                    icon={<IconAlertCircle size="1rem" />}
                    color="red"
                    title={t('eicf.page.errorTitle')}
                >
                    {signing.error}
                </Alert>
            )}
            {signing.loading && (
                <Group>
                    <Loader size="sm" />
                    <Text size="sm" c="dimmed">
                        {t('eicf.pdf.loading')}
                    </Text>
                </Group>
            )}
            <Box
                ref={signing.pagesRef}
                style={{ maxHeight: '70vh', overflow: 'auto' }}
            />
            <Group justify="flex-end">
                <Button
                    color="green"
                    onClick={signing.handleSubmit}
                    loading={signing.submitting}
                    disabled={signing.loading || !signing.signaturePlaced}
                >
                    {t('eicf.hcp.counterSignButton')}
                </Button>
            </Group>

            <SignatureAdoptModal
                opened={signing.modalOpen}
                onClose={signing.closeModal}
                defaultName={
                    consent.physicianName ?? currentUser?.displayName ?? ''
                }
                onAdopt={signing.handleAdopt}
            />
        </Stack>
    );
};

export default EICFFillablePdfCounterSigner;
